import { useState, FormEvent } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export function Contact() {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation();
  const { ref: formRef, isVisible: formVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: infoRef, isVisible: infoVisible } = useScrollAnimation({ threshold: 0.05 });

  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    barrio: '',
    consulta: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const whatsappMessage = encodeURIComponent('Me contacto del sitio web STI, mi nombre es: ');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6htm1t4ieqv1pmm5dr0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');

        const mensaje = `👋 ¡Hola! Me contacto desde la web de STI.\n\n👤 *Nombre:* ${formData.nombre}\n🏢 *Empresa / Negocio:* ${formData.empresa}\n📍 *Barrio / Localidad:* ${formData.barrio}\n💬 *Consulta:* ${formData.consulta}`;
        const waUrl = `https://wa.me/5491140402887?text=${encodeURIComponent(mensaje)}`;
        window.open(waUrl, '_blank');

        setFormData({ nombre: '', empresa: '', barrio: '', consulta: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contacto" className="py-20 bg-gray-900 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(0,255,240,0.06),transparent_60%)]"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div ref={titleRef} className={`text-center mb-16 anim-fade-up ${titleVisible ? 'visible' : ''}`}>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Solicitar <strong className="text-neon-400 text-neon-glow-sm">Presupuesto</strong>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Contactanos para recibir un presupuesto personalizado de nuestros <strong className="text-gray-200">servicios técnicos informáticos</strong>
          </p>
          <div className="neon-line max-w-xs mx-auto mt-6 opacity-40"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div ref={formRef} className={`anim-fade-left ${formVisible ? 'visible' : ''}`}>

            {/* Encabezado del formulario */}
            <div className="mb-6 p-4 bg-neon-400/5 border border-neon-400/20 rounded-xl">
              <p className="text-sm text-gray-300 leading-relaxed">
                <span className="text-xl mr-1">👋</span>
                <strong className="text-white">¡Hola! Gracias por contactarte desde la web de STI</strong><br />
                <span className="text-gray-400 text-xs mt-1 block">Por favor no omitir ningún campo, para una mejor comunicación.</span>
              </p>
            </div>

            <form id="contact-form-sti" onSubmit={handleSubmit} className="space-y-5" data-readdy-form>

              {/* Nombre */}
              <div>
                <label htmlFor="nombre" className="block text-sm font-medium text-gray-300 mb-2">
                  <span className="mr-1">👤</span> Nombre *
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm bg-gray-800/80 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-neon-400/50 focus:border-neon-400/50 outline-none transition-all placeholder-gray-500"
                  placeholder="Tu nombre completo"
                />
              </div>

              {/* Empresa */}
              <div>
                <label htmlFor="empresa" className="block text-sm font-medium text-gray-300 mb-2">
                  <span className="mr-1">🏢</span> Nombre de la Empresa / Negocio *
                </label>
                <input
                  type="text"
                  id="empresa"
                  name="empresa"
                  value={formData.empresa}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm bg-gray-800/80 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-neon-400/50 focus:border-neon-400/50 outline-none transition-all placeholder-gray-500"
                  placeholder="Nombre de tu empresa o negocio"
                />
              </div>

              {/* Barrio / Localidad */}
              <div>
                <label htmlFor="barrio" className="block text-sm font-medium text-gray-300 mb-2">
                  <span className="mr-1">📍</span> Barrio / Localidad *
                </label>
                <input
                  type="text"
                  id="barrio"
                  name="barrio"
                  value={formData.barrio}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm bg-gray-800/80 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-neon-400/50 focus:border-neon-400/50 outline-none transition-all placeholder-gray-500"
                  placeholder="Ej: Palermo, San Isidro, Vicente López..."
                />
              </div>

              {/* Consulta */}
              <div>
                <label htmlFor="consulta" className="block text-sm font-medium text-gray-300 mb-2">
                  <span className="mr-1">💬</span> Consulta: ¿En qué podemos ayudarte? *
                </label>
                <textarea
                  id="consulta"
                  name="consulta"
                  value={formData.consulta}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 text-sm bg-gray-800/80 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-neon-400/50 focus:border-neon-400/50 outline-none transition-all resize-none placeholder-gray-500"
                  placeholder="Contanos sobre tu necesidad o problema técnico..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.consulta.length}/500 caracteres</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gray-700 text-white text-base font-semibold rounded-lg border border-neon-400/50 hover:bg-gray-600 hover:border-neon-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer btn-magnetic"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-lg"></i> Enviando...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-send-plane-line text-lg"></i> Enviar Consulta
                  </span>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-neon-400/10 border border-neon-400/30 rounded-lg">
                  <p className="text-sm text-neon-400 flex items-center gap-2">
                    <i className="ri-check-double-line text-lg"></i>
                    ¡Mensaje enviado! Te redirigimos a WhatsApp para continuar la conversación. 🚀
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-sm text-red-400 flex items-center gap-2">
                    <i className="ri-error-warning-line text-lg"></i>
                    Hubo un error al enviar. Por favor, intentá nuevamente.
                  </p>
                </div>
              )}
            </form>
          </div>

          <div ref={infoRef} className={`space-y-8 anim-fade-right ${infoVisible ? 'visible' : ''}`}>
            <div className="bg-gray-800/60 rounded-2xl p-8 border border-gray-700 hover-lift">
              <h3 className="text-2xl font-bold text-white mb-6">
                Información de Contacto
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 flex items-center justify-center bg-neon-400/10 border border-neon-400/20 rounded-lg flex-shrink-0 group-hover:bg-neon-400/20 group-hover:scale-110 transition-all duration-300">
                    <i className="ri-map-pin-line text-2xl text-neon-400"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-white mb-1">Ubicación</h4>
                    <p className="text-sm text-gray-400">Maipú 18 Local 15 central<br />Puente Saavedra, Buenos Aires</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 flex items-center justify-center bg-neon-400/10 border border-neon-400/20 rounded-lg flex-shrink-0 group-hover:bg-neon-400/20 group-hover:scale-110 transition-all duration-300">
                    <i className="ri-whatsapp-line text-2xl text-neon-400"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-white mb-1">WhatsApp</h4>
                    <a href={`https://wa.me/5491140402887?text=${whatsappMessage}`} target="_blank" rel="noopener noreferrer" className="text-sm text-neon-400 hover:text-neon-300 cursor-pointer">
                      +54 9 11 4040-2887
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 flex items-center justify-center bg-neon-400/10 border border-neon-400/20 rounded-lg flex-shrink-0 group-hover:bg-neon-400/20 group-hover:scale-110 transition-all duration-300">
                    <i className="ri-phone-line text-2xl text-neon-400"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-white mb-1">Teléfono Fijo</h4>
                    <a href="tel:01140402887" className="text-sm text-neon-400 hover:text-neon-300 cursor-pointer">
                      011-4040-2887
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 flex items-center justify-center bg-neon-400/10 border border-neon-400/20 rounded-lg flex-shrink-0 group-hover:bg-neon-400/20 group-hover:scale-110 transition-all duration-300">
                    <i className="ri-time-line text-2xl text-neon-400"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-white mb-1">Horarios</h4>
                    <p className="text-sm text-gray-400">A combinar</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-neon-400/20 to-neon-600/10 rounded-2xl p-8 border border-neon-400/30 shadow-neon-sm hover-lift neon-border-animate">
              <h3 className="text-xl font-bold text-white mb-4">¿Necesitás Asistencia Inmediata?</h3>
              <p className="text-sm text-gray-300 mb-6">
                Contactanos por WhatsApp para recibir <strong className="text-white">soporte técnico</strong> inmediato y resolver tus problemas informáticos de forma rápida.
              </p>
              <a 
                href={`https://wa.me/5491140402887?text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-neon-400 text-white text-sm font-semibold rounded-lg hover:bg-neon-300 hover:text-rosa-400 transition-all shadow-neon-sm hover:shadow-neon whitespace-nowrap cursor-pointer btn-magnetic"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                Chatear Ahora
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
